import {
  column,
  FilterDefs,
  GridColumn,
  GridTableLayout,
  multiFilter,
  RowStyles,
  simpleDataRows,
  SimpleHeaderAndData,
  useGridTableLayoutState,
} from "@homebound/beam";
import { useMemo } from "react";
import { DevelopmentFilter } from "src/generated/graphqlTypes";
import { MarketDetailFragment } from "src/routes/developments/components/cohorts/CreateOrUpdateCohortModal.generated";
import {
  DevelopmentDetailFragment,
  useDevelopmentsPageMetadataQuery,
  useDevelopmentsQuery,
} from "src/routes/developments/Developments.generated";
import { createDevelopmentLotSummaryUrl } from "src/RouteUrls";
import { queryResult } from "src/utils";

export function DevelopmentsPage() {
  const query = useDevelopmentsPageMetadataQuery();
  return queryResult(query, { data: ({ markets }) => <DevelopmentsPageData markets={markets} /> });
}

export type DevelopmentsPageDataProps = { markets: MarketDetailFragment[] };

type Row = SimpleHeaderAndData<DevelopmentDetailFragment>;

export function DevelopmentsPageData({ markets }: DevelopmentsPageDataProps) {
  const filterDefs: FilterDefs<DevelopmentFilter> = useMemo(() => {
    const market = multiFilter({
      label: "Market",
      options: markets,
      getOptionLabel: ({ name }) => name,
      getOptionValue: ({ id }) => id,
    });

    return { market };
  }, [markets]);

  const layoutState = useGridTableLayoutState<DevelopmentFilter>({
    persistedFilter: { filterDefs, storageKey: "developmentFilter" },
    search: "client",
  });

  const query = useDevelopmentsQuery({ variables: { filter: layoutState.filter ?? {} } });

  return (
    <GridTableLayout
      layoutState={layoutState}
      tableProps={{
        query,
        columns,
        createRows: (data) => simpleDataRows(data?.developments),
        rowStyles,
        as: "virtual",
        sorting: { on: "client" },
        emptyFallback: "No developments found.",
      }}
    />
  );
}

const columns: GridColumn<Row>[] = [
  column<Row>({ id: "name", name: "Name", header: "Name", data: ({ name }) => name }),
  column<Row>({ id: "market", name: "Market", header: "Market", data: ({ market }) => market.name }),
  column<Row>({ id: "cohorts", name: "Cohorts", header: "Cohorts", data: ({ cohorts }) => cohorts?.length }),
];

const rowStyles: RowStyles<Row> = { header: {}, data: { rowLink: ({ id }) => createDevelopmentLotSummaryUrl(id) } };
